import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useSearchParams } from 'react-router-dom';
import Plausible from 'plausible-tracker'

import Home from './pages/Home';
import Gallery from './pages/Gallery';
import AdminPage from './pages/AdminPage';
import OAuthCallback from './pages/OAuthCallback';
import Topbar from './components/Topbar';
import { API_BASE_URL, CONNECT_UI_URL, CONNECT_CLIENT_ID, SESSION_TOKEN_KEY } from './config';
import { isEmbedded, consumeConnectError, endAllSessions, startSessionWatch, isSessionLoss } from '@globegroupe-data/auth-kit/browser';

import { removeCookie } from './utils/cookies.js';
import i18n from './i18n';

import './index.css';

const THEMES_CONFIG = {
  xiaomi: {
    logo: '/logos/SMM_NOIR.png',
    logo_client: '/logo_xiaomi.png',
    color: 'bg-white border-b border-gray-200 shadow-md',
  },
  samsung: {
    logo: '/logos/INFLUENCE.png',
    logo_client: '/logo_samsung_black.png',
    color: 'bg-white border-b border-gray-200 shadow-md',
  },
  google_hardware: {
    logo: '/logos/INFLUENCE_NOIR.png',
    logo_client: '/logo_google.png',
    color: 'bg-white border-b border-gray-200 shadow-md',
  },
  hisense: {
    logo: '/logos/TECHSELL.png',
    logo_client: '/logo_hisense.jpg',
    color: 'bg-white border-b border-gray-200 shadow-md text-black',
  },
  lg: {
    logo: '/logos/TECHSELL.png',
    logo_client: '/logo_lg.svg',
    color: 'bg-[#f0ece4] border-b border-gray-200 shadow-md text-black',
  },
  dyson: {
    logo: '/logos/TECHSELL_BLANC.png',
    logo_client: '/logo_dyson.svg',
    color: 'bg-black border-b border-gray-200 shadow-md text-white',
  },
  primerimpacto: {
    logo: '/logo_primer_impacto.png',
    color: 'bg-white border-b border-gray-200 shadow-md text-red-500',
  },
  loreal: {
    logo: '/logos/TECHSELL.png',
    logo_client: '/logo_loreal.png',
    color: 'bg-white border-b border-gray-200 shadow-md',
  },
  default: {
    logo: '/globe_groupe_logo.svg',
    logo_client: null,
    color: 'bg-white border-b border-gray-200 shadow-md',
  },
  default_dark: {
    logo: '/logos/GLOBE_GROUPE_BLANC.png',
    logo_client: null,
    color: 'bg-gray-900 border-b border-gray-700 shadow-md',
  }
}

const { trackPageview, trackEvent } = Plausible({
  // Track localhost by default
  trackLocalhost: false,
  domain: process.env.PLAUSIBLE_DOMAIN || 'gallery.globe-groupe.com',
})

// Créer un composant wrapper pour accéder aux searchParams
function AppContent() {
  const [searchParams] = useSearchParams();
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  // Not persisted: the preview lasts as long as the page. `?as_client` forces it on load, so a
  // demo can be opened straight in the client's view without anyone doing the switch on stage.
  const [clientView, setClientView] = useState(
    () => new URLSearchParams(window.location.search).get('as_client') !== null
  );
  const [currentAppliedSearch, setCurrentAppliedSearch] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  // In an iframe, ask Connect for a SILENT authorization (silent=1, no form in
  // the frame); on failure the callback bounces back with ?connect_error.
  const redirectToConnect = ({ silent = isEmbedded() } = {}) => {
    const returnTo = encodeURIComponent(window.location.href);
    window.location.assign(`${API_BASE_URL}/auth/login?returnTo=${returnTo}${silent ? '&silent=1' : ''}`);
  };

  const clearAuthState = () => {
    localStorage.removeItem(SESSION_TOKEN_KEY);
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  // Jeton du flux fournisseur externe (primer-impacto) UNIQUEMENT — un JWT signé
  // par le backend, jamais des identifiants. localStorage seul : plus aucun cookie
  // n'entre dans la construction d'un header Authorization, c'est ce qui permettait
  // au cookie legacy `authToken` d'une app non migrée de saboter la reconnexion
  // silencieuse et de changer d'identité.
  const retrieveSessionToken = () => localStorage.getItem(SESSION_TOKEN_KEY);

  // A 401 once signed in means the session died elsewhere. Embed contract: inside
  // a frame we make ONE silent re-login attempt (definitive failure comes back as
  // ?connect_error and shows the placeholder) — never a takeover screen, the parent
  // shell judges logout. Top-level: interactive login. Only armed once
  // authenticated: the mount probe owns the 401 that means "no session yet".
  const onSessionExpired = () => {
    if (!isAuthenticated) return;
    if (isEmbedded()) {
      redirectToConnect({ silent: true });
      return;
    }
    redirectToConnect({ silent: false });
  };

  const fetchWithAuth = async (url, options = {}) => {
    const token = retrieveSessionToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      credentials: options.credentials || 'include',
      headers
    });

    // isSessionLoss et pas le statut : seuls les 401 portant le challenge
    // `WWW-Authenticate: Session` viennent de la couche session. Les routes admin
    // (statistics, settings) répondent 401 « Admin privileges required » — sur le
    // statut seul, un non-admin qui les touchait se faisait déconnecter.
    if (isSessionLoss(response)) {
      if (!options.noLogOut) {
        clearAuthState();
        onSessionExpired();
      }

      const error = new Error('Authentication failed');
      error.response = response;
      throw error;
    }

    // 401 applicatif (sans challenge) : refus de CE call, pas une session morte.
    // Même logique que le 403 ci-dessous : on n'éjecte personne.
    if (response.status === 401) {
      const error = new Error('Authentication failed');
      error.response = response;
      throw error;
    }

    // A 403 is not a dead session, so it must not bounce to Connect — that looped.
    // But it must not leave an authenticated-looking page either: the original code
    // cleared the state here, and dropping that let a stale UI survive.
    if (response.status === 403) {
      if (!options.noLogOut) {
        clearAuthState();
        setAuthError(`Authentification impossible (HTTP ${response.status}).`);
      }

      const error = new Error('Forbidden');
      error.response = response;
      throw error;
    }

    return response;
  };

  const logout = async () => {
    // Déconnexion = déconnexion : la session SSO tombe aussi, sinon les autres apps
    // resteraient connectées.
    await endAllSessions({
      apiBaseUrl: API_BASE_URL,
      connectUrl: CONNECT_UI_URL,
      clientId: CONNECT_CLIENT_ID,
    });

    clearAuthState();

    // Jamais de formulaire de login dans un cadre : l'écran de reconnexion rend la
    // fenêtre du haut à l'app parente.
    if (isEmbedded()) {
      setSessionExpired(true);
      return;
    }

    redirectToConnect({ silent: false });
  };

  // Focus-only (auth-kit 0.10.0): user action + tab focus. When embedded, no watch
  // at all — the parent shell (cockpit) watches the session and takes the whole
  // page to the login when it dies; the frame only heals itself silently through
  // its 401 handler.
  useEffect(() => {
    if (!isAuthenticated || isEmbedded()) return undefined;
    return startSessionWatch({
      probeUrl: `${API_BASE_URL}/api/me`,
      onSessionLost: onSessionExpired,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Check authentication status and fetch user data on mount
  useEffect(() => {
    // Purge du jeton de l'ancienne auth Basic : base64(login:motdepasse), le mot de
    // passe en clair, décodable. Plus rien ne le lit ici, mais des apps non migrées
    // (Weekly) le posent encore sur tout .globe-groupe.com — laissé en place, il
    // basculait Gallery sur son repli legacy à la première session morte (mauvaise
    // identité, reconnexion silencieuse sabotée). Suppression à chaque montage tant
    // que ces apps existent.
    localStorage.removeItem('authToken');
    removeCookie('authToken');

    // ?connect_error = failed silent login (no SSO session): reconnect screen in
    // an iframe, interactive login top-level. consumeConnectError cleans the URL.
    if (consumeConnectError()) {
      if (isEmbedded()) {
        setSessionExpired(true);
        setIsAuthenticating(false);
        return;
      }
      redirectToConnect({ silent: false });
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/me`);

        // 401 and 403 already threw inside fetchWithAuth, with the response attached. Anything left
        // is a 5xx — carry the status so the catch below shows the error instead of restarting a
        // login: retrying a login against a backend that is down loops for the whole incident.
        if (!response.ok) {
          const error = new Error('Authentication failed');
          error.response = response;
          throw error;
        }

        const userData = await response.json();
        setCurrentUser(userData);
        setIsAuthenticated(true);

        i18n.changeLanguage(localStorage.getItem('locale') || userData.locale);

        trackPageview({
          props: {
            user: userData?.username,
            client: userData?.client,
          }
        });

        trackEvent('access', {
          props: {
            user: userData?.username,
            client: userData?.client,
          }
        });

      } catch (error) {
        console.error('Auth check failed:', error);
        clearAuthState();

        // Only a 401 warrants a trip to Connect; anything else (e.g. 403 unknown
        // user) would loop forever, so show the error instead.
        const status = error?.response?.status;
        if (status && status !== 401) {
          setAuthError(`Authentification impossible (HTTP ${status}).`);
        } else {
          redirectToConnect();
        }
      }

      setTimeout(() => {
        setIsAuthenticating(false);
      }, 250);

    };

    checkAuth();
  }, []);

  const [darkMode, setDarkMode] = useState(
    localStorage.theme === 'dark' ||
    (!localStorage.theme && window.matchMedia('(prefers-color-scheme: dark)').matches)
  );

  const handleThemeToggle = (darkMode) => {
    setDarkMode(darkMode);
  }

  // SSO session dead in an iframe: the button sends the TOP window to the parent
  // app (the user's click grants the iframe that right).
  if (sessionExpired) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4 max-w-md p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <p className="text-lg font-medium text-gray-700 dark:text-gray-200">Session expirée</p>
          {/* Reloads THE FRAME: resets the memoised connect_error, hence a fresh
              silent attempt. Never window.top: ejecting the user from their cockpit
              is not a decision that belongs to a frame. */}
          <button
            className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            onClick={() => window.location.reload()}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="max-w-md p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <p className="text-red-700 dark:text-red-300">{authError}</p>
        </div>
      </div>
    );
  }

  if (isAuthenticating || (isAuthenticated && !currentUser)) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-xl font-medium text-gray-700 dark:text-gray-200">Authentification...</div>
        </div>
      </div>
    );
  }

  const theme = 'default';
  const themeConfig = (darkMode ? THEMES_CONFIG['default_dark'] : THEMES_CONFIG[theme]) || THEMES_CONFIG['default']

  // console.log('>>> themeConfig', darkMode, searchParams.get('theme'), themeConfig);

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-xl font-medium text-gray-700 dark:text-gray-200">Redirection vers Connect...</div>
        </div>
      </div>
    );
  }

  // Seeing the app as a client is done by handing the whole tree a downgraded user, so every
  // existing guard applies by itself rather than each screen learning about a preview mode. It only
  // ever removes rights, never grants any, and the server keeps deciding what it serves.
  const canViewAsClient = !!currentUser?.isadmin;
  const viewingAsClient = canViewAsClient && clientView;
  const effectiveUser = viewingAsClient
    ? { ...currentUser, isadmin: false, issuperadmin: false, multiclient: false }
    : currentUser;

  // Leaving admin is not handled here: AdminPage already sends a non-admin back to the gallery,
  // so the downgrade is enough to get out of the pages a client cannot reach.
  const toggleClientView = setClientView;

  return (
    <div className="dark:bg-gray-600 dark:text-white min-h-screen">

      <Topbar
        currentUser={effectiveUser}
        logout={logout}
        themeConfig={themeConfig}
        handleThemeToggle={handleThemeToggle}
        darkMode={darkMode}
        canViewAsClient={canViewAsClient}
        viewingAsClient={viewingAsClient}
        onToggleClientView={toggleClientView}
      />

      {/* Routes */}
      <Routes>
        <Route
          path="/"
          element={
            <Home
              fetchWithAuth={fetchWithAuth}
              currentUser={effectiveUser}
            />
          }
        />
        {['/gallery', '/gallery/share/:pictureId', '/gallery/visit/:visitPictureId'].map(path => (
          <Route
            key={path}
            path={path}
            element={
              <Gallery
                fetchWithAuth={fetchWithAuth}
                currentUser={effectiveUser}
                viewingAsClient={viewingAsClient}
                trackEvent={trackEvent}
                currentAppliedSearch={currentAppliedSearch}
                setCurrentAppliedSearch={setCurrentAppliedSearch}
              />
            }
          />
        ))}

        <Route path="/admin/*" element={<AdminPage fetchWithAuth={fetchWithAuth} currentUser={effectiveUser} />} />
      </Routes>
    </div>
  );
}

// Composant App principal modifié
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/oauth-callback" element={<OAuthCallback />} />
        <Route path="/*" element={<AppContent />} />
      </Routes>
    </Router>
  );
}

export default App;